import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Spin } from 'antd';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from 'chart.js';
import { MakeApiCall } from '../../../../../apis';
import { useThemeMode } from '../../../../../contexts/ThemeContext';
import { fetchCogsHistoryRecords } from '../../../../../utils/cogsHistoryApi';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

const CogsHistoryHoverChart = ({
    sku,
    token,
    marketplaceId,
    currentCogs,
    currentBatchStartDate,
}) => {
    const [cogsHistory, setCogsHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [usedFallback, setUsedFallback] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const { themeMode } = useThemeMode();
    const isDark = themeMode === 'dark';

    useEffect(() => {
        if (!sku || !token) {
            setCogsHistory([]);
            setUsedFallback(false);
            setErrorMessage(null);
            return;
        }

        let cancelled = false;
        setLoading(true);

        (async () => {
            try {
                const result = await fetchCogsHistoryRecords({
                    sku,
                    marketplaceId,
                    token,
                    currentCogs,
                    currentBatchStartDate,
                    makeApiCall: MakeApiCall,
                });

                if (cancelled) return;

                setCogsHistory(result.records);
                setUsedFallback(result.usedFallback);
                setErrorMessage(result.errorMessage);
            } catch {
                if (!cancelled) {
                    setCogsHistory([]);
                    setUsedFallback(false);
                    setErrorMessage(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [sku, token, marketplaceId, currentCogs, currentBatchStartDate]);

    const cogsColor = isDark ? '#f45b68' : '#314F71';
    const hasData = cogsHistory.length > 0;

    const chartData = useMemo(() => ({
        labels: hasData ? cogsHistory.map(record => record.batchStartDate) : [],
        datasets: [
            {
                label: 'COGS (Cost per unit)',
                data: hasData ? cogsHistory.map(record => record.cogs) : [],
                borderColor: cogsColor,
                borderWidth: 2.5,
                tension: 0.4,
                pointRadius: 3,
                pointHoverRadius: 6,
                pointBackgroundColor: cogsColor,
                pointBorderColor: isDark ? '#1f2937' : '#fff',
                pointBorderWidth: 1,
                fill: false,
            },
        ],
    }), [cogsHistory, cogsColor, hasData, isDark]);

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: (tooltipItems) => {
                        const index = tooltipItems[0]?.dataIndex;
                        if (cogsHistory[index]?.batchStartDate) {
                            return `Batch Start Date: ${cogsHistory[index].batchStartDate}`;
                        }
                        return tooltipItems[0]?.label;
                    },
                    label: (context) => {
                        const value = context.parsed.y;
                        if (value === null || value === undefined) return 'COGS (Cost per unit): —';
                        return `COGS (Cost per unit): ₹${value.toFixed(2)}`;
                    },
                },
                backgroundColor: isDark ? '#1f2937' : 'white',
                titleColor: isDark ? '#e5e7eb' : '#333',
                bodyColor: isDark ? '#cbd5e1' : '#666',
                borderColor: isDark ? '#3f4254' : '#ddd',
                borderWidth: 1,
                padding: 10,
                displayColors: true,
            },
            legend: {
                position: 'top',
                align: 'center',
                labels: {
                    color: isDark ? '#e5e7eb' : '#3f4254',
                    font: { size: 11 },
                    usePointStyle: true,
                    pointStyle: 'circle',
                    boxWidth: 8,
                    boxHeight: 8,
                    padding: 10,
                },
            },
        },
        scales: {
            x: {
                ticks: {
                    font: { size: 10 },
                    color: isDark ? '#a1a5b7' : '#888',
                    maxRotation: 45,
                    autoSkip: true,
                    maxTicksLimit: 8,
                },
                grid: { display: false },
            },
            y: {
                position: 'left',
                ticks: {
                    callback: (value) => {
                        if (value === null || value === undefined) return '—';
                        return `₹${Number(value).toFixed(2)}`;
                    },
                    font: { size: 10 },
                    color: cogsColor,
                },
                grid: {
                    color: isDark ? 'rgba(161, 165, 183, 0.28)' : '#f0f0f0',
                    drawBorder: false,
                    lineWidth: 1,
                },
            },
        },
        elements: {
            line: {
                borderJoin: 'round',
                borderCap: 'round',
            },
            point: {
                hoverRadius: 6,
                radius: 3,
            },
        },
        layout: {
            padding: { top: 10, bottom: 10, left: 10, right: 10 },
        },
    }), [cogsHistory, cogsColor, isDark]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5" style={{ minHeight: 180, minWidth: 380 }}>
                <Spin size="small" tip="Loading COGS history..." />
            </div>
        );
    }

    if (!hasData) {
        return (
            <div className="py-3" style={{ minWidth: 380 }}>
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={errorMessage || 'No COGS history found'}
                />
            </div>
        );
    }

    return (
        <div style={{ width: 420 }}>
            <div
                className="fw-bold fs-7 mb-2"
                style={{ color: isDark ? '#e5e7eb' : '#181c32' }}
            >
                COGS History
            </div>
            <div className="text-muted fs-8 mb-3">SKU: {sku}</div>
            {usedFallback && (
                <div className="text-muted fs-8 mb-2">
                    Showing current COGS value. Full history is unavailable for this account.
                </div>
            )}
            <div style={{ height: 180 }}>
                <Line key={themeMode} data={chartData} options={chartOptions} />
            </div>
        </div>
    );
};

export default CogsHistoryHoverChart;
